import Vue from 'vue';
import axios from 'axios';
import {getProxyAccessToken} from '@/plugins/cookie-plugins';
import {proxyPath, proxyBody, proxyHeaders} from '@/utils/b2bProxy';

// ─── Configuration ───────────────────────────────────────────────────────────

// How often (ms) to refresh cashout amounts for pending bets.
// Change this constant to tune the polling rate.
export const CASHOUT_POLL_INTERVAL_MS = 60000;

// How often (ms) to retry bets in 'temporary' state (CASHOUT_NOT_AVAILABLE with temporary: true).
export const CASHOUT_TEMPORARY_POLL_INTERVAL_MS = 60000;

// Statuses that warrant showing the cashout UI element (loading spinner, button, or disabled state).
export const CASHOUT_VISIBLE_STATUSES = new Set(['available', 'suspended', 'temporary', 'loading']);

// Hard ceiling on cashout requests in flight at any moment, across every
// poller. MyBets used to fan out one request per pending bet with only a blind
// 200ms stagger between launches and no cap at all, so a player with 40 pending
// bets produced 40 requests, and the 'temporary' poller fired its whole set
// simultaneously every minute. Both now go through one bounded worker pool.
export const CASHOUT_MAX_CONCURRENT = 3;
// Pacing of the pool (src/utils/cashoutWorkerPool.js): workers start
// CASHOUT_WORKER_STAGGER_MS apart, and each pauses CASHOUT_TASK_GAP_MS between
// two requests — a poll is spread over a second or two instead of firing
// every request in the same instant.
export const CASHOUT_WORKER_STAGGER_MS = 200;
export const CASHOUT_TASK_GAP_MS = 150;

// Per-request timeout. Without it a single hung request would occupy a worker
// slot forever: the pool would never resolve, the re-entrancy guard would stay
// closed, and cashout amounts would silently stop refreshing for the whole
// session. axios aborts the request on timeout and `calculateCashout` turns
// that into status 'unavailable', which the next poll retries.
export const CASHOUT_REQUEST_TIMEOUT_MS = 15000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractErrorCodes(body) {
    return (body.errors || []).map(e => e.code);
}

// Permanent errors → hide button, stop retrying.
const CASHOUT_HIDDEN_CODES = new Set([
    'BET_TYPE_NOT_VALID', 'ANTEPOST', 'ODD_OUT_OF_RANGE',
    'BET_ALREADY_LOST', 'ALL_EVENTS_FINISHED',
    'CASHOUT_DISABLED', 'MIN_EVENTS_NOT_REACHED', 'MAX_EVENTS_EXCEEDED',
    'BET_NOT_FOUND', 'UNAUTHORIZED', 'CASHOUT_CONFIG_LOAD_ERROR',
    'CASHOUT_IS_ONLY_AVAILABLE_FOR_PLAYERS',
    'BET_BUILDER_CASHOUT_NOT_ALLOWED',
    'BONUS_BET_CASHOUT_NOT_ALLOWED',
    'BET_FREEZE_CASHOUT_NOT_ALLOWED',
]);

// Temporary errors → show dimmed button + tooltip, retry on next poll.
const CASHOUT_SUSPENDED_CODES = new Set([
    'CASHOUT_NOT_AVAILABLE_YET', 'ODDS_SUSPENDED', 'SOME_EVENTS_SUSPENDED',
    'CASHOUT_SUSPENDED', 'OUTCOME_LAST_UPDATE_OVERDUE', 'PARTIAL_OUTCOME',
]);

/**
 * Maps API error codes to { status, errorCode? }.
 * Priority: hidden > GLOBAL_*_DISABLED > suspended > unavailable (retry)
 */
export function statusFromErrorCodes(errorCodes) {
    for (const code of errorCodes) {
        if (CASHOUT_HIDDEN_CODES.has(code)) return { status: 'hidden' };
        if (code.startsWith('GLOBAL_') && code.endsWith('_DISABLED')) {
            return { status: 'suspended', errorCode: code };
        }
    }
    for (const code of errorCodes) {
        if (CASHOUT_SUSPENDED_CODES.has(code)) return { status: 'suspended', errorCode: code };
    }
    return { status: 'unavailable' };
}

// ─── State ───────────────────────────────────────────────────────────────────

// CashoutEntry shape:
// {
//   status: 'idle' | 'loading' | 'available' | 'suspended' | 'temporary' | 'hidden' | 'unavailable' | 'confirmed'
//   amount?: number
//   errorCode?: string        (set when status === 'suspended')
//   hasActiveLiveEvents?: boolean
//   hasSettledWonEvents?: boolean
// }
// 'temporary': CASHOUT_NOT_AVAILABLE with temporary:true → disabled button, auto-retry every 30 s

const state = {
    cashouts: {}, // keyed by bet.code (always the long code)
};

// ─── Getters ─────────────────────────────────────────────────────────────────

const getters = {
    getCashoutByCode: (state) => (code) => state.cashouts[code] || {status: 'idle'},
};

// ─── Mutations ───────────────────────────────────────────────────────────────

const mutations = {
    SET_CASHOUT(state, {code, data}) {
        Vue.set(state.cashouts, code, data);
    },

    CLEAR_CASHOUTS(state) {
        state.cashouts = {};
    },
};

// ─── Actions ─────────────────────────────────────────────────────────────────

const actions = {
    /**
     * Calls /cart/v3/calculate-cashout and updates state for the given bet code.
     * Always pass bet.code (the long code) — never the short betCode.
     */
    async calculateCashout({commit, rootGetters}, {code, bypassCache = false}) {
        commit('SET_CASHOUT', {code, data: {status: 'loading'}});

        const proxyUrl = rootGetters['settings/getProxyUrl'];
        const url = `${proxyUrl}${proxyPath(rootGetters, '/cart/v3/calculate-cashout')}`;
        const token = getProxyAccessToken();

        try {
            const payload = proxyBody(rootGetters, bypassCache ? {code, bypassCache: true} : {code});
            const {data: body} = await axios.post(url, payload, {
                headers: proxyHeaders(rootGetters, token),
                // Bounds how long a worker slot can be held — see the constant.
                timeout: CASHOUT_REQUEST_TIMEOUT_MS,
            });

            if (body.result) {
                const events = (body.cashoutData && body.cashoutData.events) || [];

                const hasActiveLiveEvents = events.some(e => e.seconds > 0);
                const hasSettledWonEvents = events.some(e => e.outcomeResult !== null);

                commit('SET_CASHOUT', {
                    code, data: {
                        status: 'available',
                        amount: body.cashoutData.cashoutAmount ?? null,
                        events,
                        hasActiveLiveEvents,
                        hasSettledWonEvents,
                        lastUpdated: Date.now(),
                    }
                });
            } else {
                const errors = body.errors || [];
                const isTemporary = errors.some(e => e.code === 'CASHOUT_NOT_AVAILABLE' && e.temporary === true);
                if (isTemporary) {
                    commit('SET_CASHOUT', { code, data: { status: 'temporary', lastUpdated: Date.now() } });
                } else {
                    const { status, errorCode } = statusFromErrorCodes(extractErrorCodes(body));
                    const data = { status, lastUpdated: Date.now() };
                    if (errorCode) data.errorCode = errorCode;
                    // Event that blocks the cashout (shown under the disabled button).
                    const suspendedDetail = errors.map(e => e.suspendedOddsDetails && e.suspendedOddsDetails[0]).find(Boolean);
                    if (suspendedDetail && suspendedDetail.eventName) data.suspendedEventName = suspendedDetail.eventName;
                    commit('SET_CASHOUT', { code, data });
                }
            }
        } catch {
            commit('SET_CASHOUT', {code, data: {status: 'unavailable', lastUpdated: Date.now()}});
        }
    },

    /**
     * Calls /cart/v3/cashout to confirm the cashout.
     * Returns { success: boolean, frozenExpired?: boolean }.
     * FROZEN_EXPIRED means the ~5 s freeze window passed; caller must recalculate.
     */
    async executeCashout({commit, rootGetters}, {code}) {
        const proxyUrl = rootGetters['settings/getProxyUrl'];
        const url = `${proxyUrl}${proxyPath(rootGetters, '/cart/v3/cashout')}`;
        const token = getProxyAccessToken();

        try {
            const {data: body} = await axios.post(url, proxyBody(rootGetters, {code}), {
                headers: proxyHeaders(rootGetters, token),
            });

            if (body.cashoutConfirmed) {
                commit('SET_CASHOUT', {code, data: {status: 'confirmed'}});
                return {success: true};
            }

            const errorCodes = extractErrorCodes(body);

            if (errorCodes.includes('FROZEN_EXPIRED')) {
                return {success: false, frozenExpired: true};
            }

            commit('SET_CASHOUT', {code, data: {status: 'unavailable'}});
            return {success: false, errorCodes};
        } catch {
            commit('SET_CASHOUT', {code, data: {status: 'unavailable'}});
            return {success: false};
        }
    },
};

export default {namespaced: true, state, getters, mutations, actions};
